import { useCallback, useEffect, useState } from 'react';
import { Check, Loader2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { projectId } from '../utils/supabase/info';

/**
 * EngagementObjectivesDialog — "Refine objectives" for the firm (RESTORE_PLAN R2.6): every
 * strategic objective (HLO) of the engagement with where it came from and its status; approve a
 * draft, remove one, or define a new one. Carries the Command Center's "Define Objective" and
 * the Activation panel's objectives list. Routes: `GET|POST /engagements/:id/hlos`,
 * `PUT /engagements/:id/hlos/:hloId`.
 */

export interface HloRecord {
  id: string;
  title: string;
  description?: string;
  category?: string;
  source?: string;
  status?: 'draft' | 'approved' | 'removed' | string;
  confidence?: number;
}

export const HLO_CATEGORIES = ['efficiency', 'automation', 'user_experience', 'reporting', 'compliance', 'integration', 'other'] as const;

const categoryLabel = (c?: string) => (c ? c.replace(/_/g, ' ').replace(/^./, (x) => x.toUpperCase()) : 'Other');
const sourceLabel = (s?: string) => (s === 'ai_extracted' ? 'From the SOW (AI)' : s === 'client_added' ? 'Added by the client' : 'Added by the firm');

interface EngagementObjectivesDialogProps {
  open: boolean;
  onClose: () => void;
  engagementId: string;
  accessToken: string;
  currentUser: any;
  /** After any change, so the page can reload what it shows. */
  onChanged?: () => void;
}

export function EngagementObjectivesDialog({ open, onClose, engagementId, accessToken, currentUser, onChanged }: EngagementObjectivesDialogProps) {
  const base = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/engagements/${encodeURIComponent(engagementId)}/hlos`;
  const [hlos, setHlos] = useState<HloRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<string>('other');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(base, { headers: { Authorization: `Bearer ${accessToken}` } });
      const data = await res.json().catch(() => ({}));
      setHlos(res.ok ? (data.hlos ?? []) : []);
    } catch {
      setHlos([]);
    } finally {
      setLoading(false);
    }
  }, [base, accessToken]);

  useEffect(() => { if (open) void load(); }, [open, load]);

  const byName = currentUser?.name || currentUser?.email || 'Consultant';

  const setStatus = async (hlo: HloRecord, status: 'approved' | 'removed') => {
    setBusy(hlo.id);
    try {
      const res = await fetch(`${base}/${encodeURIComponent(hlo.id)}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(status === 'approved' ? { status, approvedByName: byName } : { status }),
      });
      if (!res.ok) throw new Error();
      setHlos((prev) => prev.map((h) => (h.id === hlo.id ? { ...h, status } : h)));
      onChanged?.();
    } catch {
      toast.error('Could not update the objective');
    } finally {
      setBusy(null);
    }
  };

  const add = async () => {
    if (!title.trim()) return;
    setBusy('new');
    try {
      const res = await fetch(base, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: title.trim(), description: description.trim(), category, source: 'consultant_added', createdByName: byName }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.hlo) throw new Error();
      setHlos((prev) => [...prev, data.hlo]);
      setTitle('');
      setDescription('');
      setCategory('other');
      toast.success('Objective added');
      onChanged?.();
    } catch {
      toast.error('Could not save the objective');
    } finally {
      setBusy(null);
    }
  };

  const active = hlos.filter((h) => h.status !== 'removed');

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Refine objectives</DialogTitle>
          <DialogDescription>The engagement's strategic objectives. Approve the drafts you agree with, remove what does not belong, and add what is missing.</DialogDescription>
        </DialogHeader>

        {loading ? (
          <p className="flex items-center gap-2 text-muted-foreground"><Loader2 className="size-4 animate-spin" aria-hidden="true" /> Loading objectives…</p>
        ) : active.length === 0 ? (
          <p className="text-muted-foreground">No objectives yet. Upload the SOW to extract them, or add one below.</p>
        ) : (
          <ul role="list" className="divide-y rounded-md border" data-testid="objective-list">
            {active.map((h) => (
              <li key={h.id} className="flex flex-col gap-2 p-3 sm:flex-row sm:items-start" data-testid="objective">
                <div className="min-w-0 flex-1 space-y-1">
                  <p>{h.title}</p>
                  {h.description && <p className="text-muted-foreground">{h.description}</p>}
                  <div className="flex flex-wrap gap-1.5">
                    <Badge variant="outline">{categoryLabel(h.category)}</Badge>
                    <Badge variant="outline">{sourceLabel(h.source)}</Badge>
                    <Badge variant={h.status === 'approved' ? 'default' : 'secondary'}>{h.status === 'approved' ? 'Approved' : 'Draft'}</Badge>
                  </div>
                </div>
                <div className="flex shrink-0 gap-2">
                  {h.status !== 'approved' && (
                    <Button size="sm" variant="outline" disabled={busy === h.id} onClick={() => void setStatus(h, 'approved')} aria-label={`Approve ${h.title}`}>
                      <Check className="mr-1 size-4" aria-hidden="true" /> Approve
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" disabled={busy === h.id} onClick={() => void setStatus(h, 'removed')} aria-label={`Remove ${h.title}`}>
                    <X className="mr-1 size-4" aria-hidden="true" /> Remove
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <form className="space-y-2 border-t pt-4" onSubmit={(e) => { e.preventDefault(); void add(); }} data-testid="objective-add">
          <p>Define an objective</p>
          <div className="space-y-1">
            <Label htmlFor="objective-title">Objective</Label>
            <Input id="objective-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Cut first-response time on incidents to under 4 hours" maxLength={200} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="objective-description">Why it matters (optional)</Label>
            <Textarea id="objective-description" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} maxLength={1000} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="objective-category">Category</Label>
            <select id="objective-category" value={category} onChange={(e) => setCategory(e.target.value)} className="h-9 w-full rounded-md border bg-white px-2">
              {HLO_CATEGORIES.map((c) => <option key={c} value={c}>{categoryLabel(c)}</option>)}
            </select>
          </div>
          <Button type="submit" size="sm" disabled={busy === 'new' || !title.trim()}>
            {busy === 'new' ? <Loader2 className="mr-2 size-4 animate-spin" aria-hidden="true" /> : <Plus className="mr-2 size-4" aria-hidden="true" />}
            Add objective
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
